import { useEffect } from 'react';
import { createPath } from '~/pages/onboarding/Sider';
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { onboarding } from '~/routes';
import { history } from '..';
import useMutableCallback from './useMutableCallback';
import useReduxState, { SetReduxState } from './useReduxState';

const initialState = [
  {
    pageName: 'personalInfo' as const,
    disabled: false,
    visible: true,
    valid: true,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'personalInfo/confirm' as const,
    disabled: true,
    visible: false,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'personalInfo/authenticate' as const,
    disabled: false,
    visible: false,
    valid: null,
    skip: true,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'country' as const,
    disabled: true,
    visible: true,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'companyInformation' as const,
    disabled: true,
    visible: true,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'companyInformation/contacts' as const,
    disabled: true,
    visible: false,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'companyAddress' as const,
    disabled: true,
    visible: true,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'createAccount' as const,
    disabled: true,
    visible: true,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'createAccount/confirm' as const,
    disabled: true,
    visible: false,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'createAccount/password' as const,
    disabled: true,
    visible: false,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
  {
    pageName: 'accountExist' as const,
    disabled: true,
    visible: false,
    valid: null,
    skip: false,
    formData: null,
    relatedData: null,
  },
];

export type PageNames = typeof initialState[0]['pageName'];
export type OnboardingStateType<D = any, R = any> = {
  pageName: PageNames;
  disabled: boolean; // sider menu item is disabled
  visible: boolean; // sider menu item is visible
  valid: boolean | null; // is page valid
  skip: boolean; // skip page
  formData: D | null;
  relatedData: R | null;
};

type SetScopedStateFunction<D, R> = (
  values:
    | OnboardingStateType<D, R>
    | ((prev: OnboardingStateType<D, R>) => OnboardingStateType<D, R>)
) => void;
type SetStateFunction<D, R> = (pageName: PageNames) => SetScopedStateFunction<D, R>;

function useOnboardingState<D = any, R = any>(): [
  OnboardingStateType<D, R>[],
  SetStateFunction<D, R>,
  SetReduxState<OnboardingStateType<D, R>[]>
];
// eslint-disable-next-line no-redeclare
function useOnboardingState<D = any, R = any>(
  defaultPageName: PageNames
): [
  OnboardingStateType<D, R>,
  SetScopedStateFunction<D, R>,
  SetReduxState<OnboardingStateType<D, R>[]>
];
// eslint-disable-next-line no-redeclare
function useOnboardingState<D = any, R = any>(defaultPageName?: PageNames): any {
  const [state, setState] = useReduxState<OnboardingStateType<D, R>[]>(onboarding, initialState);

  const setStateByPageName: SetStateFunction<D, R> = (pageName) => (values) => {
    setState((prev) => {
      return prev.map((item) => {
        if (item.pageName === pageName) {
          if (typeof values === 'function') return values(item);
          return values;
        }
        return item;
      });
    });
  };

  if (defaultPageName)
    return [
      state?.find((item) => item.pageName === defaultPageName) || [],
      setStateByPageName(defaultPageName),
    ];
  return [state || [], setStateByPageName, setState];
}

export { useOnboardingState };

export const useResetOnboardingState = () => {
  const [, setState] = useReduxState<OnboardingStateType[]>(onboarding, initialState);
  return () => setState(initialState);
};

export const getFirstUnskipPage = (allPages: OnboardingStateType[]) => {
  return allPages.filter((item) => !item.skip)[0].pageName;
};
const getNextPage = (pageName: PageNames, allPages: OnboardingStateType[]) => {
  const allUnsuitablePages = allPages.filter((item) => !item.skip);
  const currentUnskipablePageIndex = allUnsuitablePages.findIndex(
    (item) => item.pageName === pageName
  );
  return allUnsuitablePages[currentUnskipablePageIndex + 1]?.pageName;
};

const getPrevPage = (pageName: PageNames, allPages: OnboardingStateType[]) => {
  const allUnskipablePages = allPages.filter((item) => !item.skip);
  const currentUnskipablePageIndex = allUnskipablePages.findIndex(
    (item) => item.pageName === pageName
  );
  return allUnskipablePages[currentUnskipablePageIndex - 1]?.pageName;
};

const getPrevVisiblePage = (pageName: PageNames, allPages: OnboardingStateType[]) => {
  const allUnskipablePages = allPages.filter((item) => !item.skip);
  const currentUnskipablePageIndex = allUnskipablePages.findIndex(
    (item) => item.pageName === pageName
  );
  for (let i = currentUnskipablePageIndex - 1; i >= 0; i--) {
    if (allUnskipablePages[i].visible) {
      return allUnskipablePages[i].pageName;
    }
  }
  return undefined;
  // return allUnskipablePages[currentUnskipablePageIndex - 1]?.pageName;
};

export const useOnboardingPage = (pageName: PageNames) => {
  const [onboardingState, setOnboardingState] = useOnboardingState();
  const [currentPageState] = useOnboardingState(pageName);

  const nextPageName = getNextPage(pageName, onboardingState);
  const prevPageName = getPrevPage(pageName, onboardingState);
  const prevVisiblePageName = getPrevVisiblePage(pageName, onboardingState);

  const activateNext = useMutableCallback(() => {
    if (nextPageName) {
      setOnboardingState(nextPageName)((prev) => ({
        ...prev,
        disabled: false,
      }));
    }
  });

  const goNext = useMutableCallback(() => history.push(createPath(nextPageName)));
  const goBack = useMutableCallback(() => history.push(createPath(prevPageName)));
  const goBackToVisible = useMutableCallback(() => history.push(createPath(prevVisiblePageName)));
  const goFirst = useMutableCallback(() => history.push(createPath(initialState[0].pageName)));

  useEffect(() => {
    if (currentPageState.disabled && nextPageName) goNext();
  }, [currentPageState.disabled, goNext, nextPageName]);

  return { activateNext, goNext, goFirst, goBack, goBackToVisible };
};

export const useSkipSteps = () => {
  const [, setOnboardingState] = useOnboardingState();

  return {
    phoneNumber: (skip: boolean) => {
      setOnboardingState('personalInfo')((prev) => ({
        ...prev,
        skip,
        visible: !skip,
        disabled: skip,
      }));
      setOnboardingState('personalInfo/confirm')((prev) => ({
        ...prev,
        skip,
        disabled: skip,
      }));
      setOnboardingState('country')((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    authenticate: (skip: boolean) => {
      setOnboardingState('personalInfo/authenticate')((prev) => ({
        ...prev,
        skip,
      }));
    },
    email: (skip: boolean) => {
      setOnboardingState('createAccount')((prev) => ({
        ...prev,
        skip,
        visible: !skip,
      }));
      setOnboardingState('createAccount/confirm')((prev) => ({
        ...prev,
        skip,
      }));
    },
    password: (skip: boolean) => {
      setOnboardingState('createAccount/password')((prev) => ({
        ...prev,
        skip,
      }));
    },
    businessCreated: (skip: boolean) => {
      setOnboardingState('country')((prev) => ({
        ...prev,
        skip,
        visible: !skip,
      }));
      setOnboardingState('companyInformation')((prev) => ({
        ...prev,
        skip,
        visible: !skip,
      }));
      setOnboardingState('companyInformation/contacts')((prev) => ({
        ...prev,
        skip,
      }));
      setOnboardingState('companyAddress')((prev) => ({
        ...prev,
        skip,
        visible: !skip,
      }));
    },
    accountExist: (skip: boolean) => {
      setOnboardingState('accountExist')((prev) => ({
        ...prev,
        skip: !skip,
        visible: skip,
      }));
    },
  };
};
